/*
  RedHeal Lab – «Forskningen i tall»
  Shared data loader (window.RHLab) + <rh-lab-chart> custom element.
  Vanilla JS + SVG, no dependencies. Data: assets/rh-lab-data.json (see tools/rh-lab/README.md).
*/
window.RHLab = window.RHLab || {};
if (!window.RHLab.loadData) {
  const cache = new Map();
  window.RHLab.loadData = (url) => {
    if (!cache.has(url)) cache.set(url, fetch(url, { credentials: 'omit' }).then((r) => { if (!r.ok) throw new Error('rh-lab data ' + r.status); return r.json(); }));
    return cache.get(url);
  };
  window.RHLab.fmt = (n) => new Intl.NumberFormat('nb-NO').format(n);
}

(() => {
  if (customElements.get('rh-lab-chart')) return;

  const SVGNS = 'http://www.w3.org/2000/svg';
  const MOBILE = 560;
  const fmt = (n) => window.RHLab.fmt(n);
  const pctFmt = new Intl.NumberFormat('nb-NO', { style: 'percent', maximumFractionDigits: 0 });
  const pct = (x) => pctFmt.format(x);
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const esc = (s) =>
    String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const reducedMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let uid = 0;

  function svgEl(tag, attrs, parent) {
    const node = document.createElementNS(SVGNS, tag);
    if (attrs) for (const k in attrs) if (attrs[k] !== undefined && attrs[k] !== null) node.setAttribute(k, attrs[k]);
    if (parent) parent.appendChild(node);
    return node;
  }

  function htmlEl(tag, className, text, parent) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    if (parent) parent.appendChild(node);
    return node;
  }

  function niceStep(raw) {
    const p = Math.pow(10, Math.floor(Math.log10(raw)));
    const f = raw / p;
    return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * p;
  }

  function niceScale(max, ticks) {
    const step = niceStep(Math.max(max, 1) / ticks);
    return { max: Math.ceil(max / step) * step, step };
  }

  /* Column with 4px rounded data-end, square at the baseline */
  function colPath(x, y, w, h, r) {
    if (h <= 0) return '';
    r = Math.min(r, w / 2, h);
    return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`;
  }

  class RHLabChart extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this.uid = ++uid;
      this.type = this.dataset.type;
      this.plot = this.querySelector('[data-plot]');
      this.statusEl = this.querySelector('[data-status]');
      if (!this.plot) return;

      if (this.data) {
        this.renderAll(false);
        return;
      }

      const src = this.dataset.src;
      if (!src) return this.fail();
      window.RHLab.loadData(src)
        .then((data) => {
          this.data = data;
          this.whenVisible(() => this.renderAll(!reducedMotion()));
        })
        .catch((err) => {
          console.warn('[rh-lab-chart]', err);
          this.fail();
        });
    }

    disconnectedCallback() {
      this._connected = false;
      if (this._io) this._io.disconnect();
      if (this._ro) this._ro.disconnect();
      this._io = this._ro = null;
    }

    whenVisible(fn) {
      if (!('IntersectionObserver' in window)) return fn();
      this._io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            this._io.disconnect();
            this._io = null;
            fn();
          }
        },
        { rootMargin: '0px 0px -12% 0px' }
      );
      this._io.observe(this);
    }

    fail() {
      this.classList.add('is-error');
      if (this.statusEl) this.statusEl.textContent = this.dataset.errorText || 'Grafen kunne ikke lastes inn akkurat nå.';
    }

    renderAll(animate) {
      this.animate = animate;
      try {
        this.render();
      } catch (err) {
        console.warn('[rh-lab-chart]', err);
        return this.fail();
      }
      this.animate = false;
      this.classList.add('is-ready');
      if (this.dataset.table !== 'false' && !this.querySelector('.rhlab-table')) this.buildTable();

      if ((this.type === 'per_year' || this.type === 'wavelengths') && 'ResizeObserver' in window && !this._ro) {
        this._w = this.plot.clientWidth;
        let frame = 0;
        this._ro = new ResizeObserver(() => {
          const w = this.plot.clientWidth;
          if (Math.abs(w - this._w) < 2) return;
          this._w = w;
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => this.render());
        });
        this._ro.observe(this.plot);
      }
    }

    render() {
      const d = this.data;
      switch (this.type) {
        case 'per_year':
          return this.renderPerYear(d);
        case 'pyramid':
          return this.renderPyramid(d);
        case 'wavelengths':
          return this.renderWavelengths(d);
        case 'countries':
          return this.renderCountries(d);
        case 'results':
          return this.renderResults(d);
        default:
          throw new Error('unknown chart type ' + this.type);
      }
    }

    /* ---------- shared plumbing ---------- */

    clearPlot() {
      [...this.plot.children].forEach((c) => {
        if (c !== this.statusEl) c.remove();
      });
      this.plot.removeAttribute('tabindex');
      this.plot.removeAttribute('role');
      this.plot.removeAttribute('aria-label');
      this.plot.onkeydown = this.plot.onblur = null;
      this._items = null;
    }

    newSvg(label) {
      const W = this.plot.clientWidth;
      const H = this.plot.clientHeight;
      const svg = svgEl('svg', {
        class: 'rhlab-svg',
        viewBox: `0 0 ${W} ${H}`,
        width: W,
        height: H,
        role: 'img',
        'aria-label': label,
        focusable: 'false',
      });
      this.plot.appendChild(svg);
      return { svg, W, H };
    }

    legend(items) {
      let ul = this.querySelector('[data-legend]');
      if (!ul) {
        ul = htmlEl('ul', 'rhlab-chart__legend');
        ul.setAttribute('data-legend', '');
        this.insertBefore(ul, this.plot);
      }
      ul.innerHTML = items
        .map((it) => `<li><span class="rhlab-swatch ${it.cls || ''}" aria-hidden="true"></span>${esc(it.label)}</li>`)
        .join('');
    }

    /* Tooltip + keyboard layer for SVG charts. items: [{x, y, html, text, node}] */
    interactive(svg, items, hitIndex, hint) {
      this._items = items;
      const tip = htmlEl('div', 'rhlab-tip', null, this.plot);
      tip.setAttribute('aria-hidden', 'true');
      const live = htmlEl('p', 'rhlab-visually-hidden', null, this.plot);
      live.setAttribute('aria-live', 'polite');
      let active = -1;

      const show = (i, announce) => {
        if (i < 0 || i >= items.length) return hide();
        if (active >= 0 && items[active].node) items[active].node.classList.remove('is-active');
        active = i;
        const it = items[i];
        if (it.node) it.node.classList.add('is-active');
        tip.innerHTML = it.html;
        tip.classList.add('is-visible');
        const W = this.plot.clientWidth;
        const tw = tip.offsetWidth;
        tip.style.left = clamp(it.x, tw / 2, W - tw / 2) + 'px';
        tip.style.top = it.y + 'px';
        if (announce) live.textContent = it.text;
      };
      const hide = () => {
        if (active >= 0 && items[active] && items[active].node) items[active].node.classList.remove('is-active');
        active = -1;
        tip.classList.remove('is-visible');
      };

      const pos = (e) => {
        const r = svg.getBoundingClientRect();
        return hitIndex(e.clientX - r.left, e.clientY - r.top);
      };
      svg.addEventListener('pointermove', (e) => show(pos(e), false));
      svg.addEventListener('pointerdown', (e) => show(pos(e), false));
      svg.addEventListener('pointerleave', (e) => {
        if (e.pointerType === 'mouse') hide();
      });

      this.plot.tabIndex = 0;
      this.plot.setAttribute('role', 'group');
      this.plot.setAttribute('aria-label', hint);
      this.plot.onkeydown = (e) => {
        let i = active;
        if (e.key === 'ArrowRight' || e.key === 'ArrowUp') i = active < 0 ? 0 : Math.min(items.length - 1, active + 1);
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') i = active < 0 ? items.length - 1 : Math.max(0, active - 1);
        else if (e.key === 'Home') i = 0;
        else if (e.key === 'End') i = items.length - 1;
        else if (e.key === 'Escape') return hide();
        else return;
        e.preventDefault();
        show(i, true);
      };
      this.plot.onblur = hide;
    }

    buildTable() {
      const t = this.tableData();
      if (!t) return;
      const details = htmlEl('details', 'rhlab-table');
      htmlEl('summary', null, this.dataset.tableLabel || 'Vis tallene', details);
      const scroll = htmlEl('div', 'rhlab-table__scroll', null, details);
      const table = htmlEl('table', null, null, scroll);
      if (t.caption) htmlEl('caption', null, t.caption, table);
      const thead = htmlEl('thead', null, null, table);
      const hr = htmlEl('tr', null, null, thead);
      t.head.forEach((h) => htmlEl('th', null, h, hr).setAttribute('scope', 'col'));
      const tbody = htmlEl('tbody', null, null, table);
      t.rows.forEach((row) => {
        const tr = htmlEl('tr', null, null, tbody);
        row.forEach((cell, i) => {
          const c = htmlEl(i === 0 ? 'th' : 'td', null, cell, tr);
          if (i === 0) c.setAttribute('scope', 'row');
        });
      });
      this.appendChild(details);
    }

    tableData() {
      const d = this.data;
      switch (this.type) {
        case 'per_year':
          return {
            head: ['År', 'Alle studier', 'Herav RCT'],
            rows: d.per_year.map((r) => [
              r.y === this.partialYear() ? `${r.y} (${this.dataset.partialLabel || 'hittil i år'})` : String(r.y),
              fmt(r.all),
              fmt(r.rct),
            ]),
          };
        case 'pyramid':
          return { head: ['Studietype', 'Antall'], rows: d.pyramid.slice().reverse().map((r) => [r.label, fmt(r.n)]) };
        case 'wavelengths': {
          const w = d.wavelengths;
          const rows = [];
          w.counts.forEach((n, i) => {
            if (!n) return;
            const a = w.from + i * w.bin;
            rows.push([`${a}–${a + w.bin - 1} nm`, fmt(n)]);
          });
          return { head: ['Bølgelengde', 'Studier'], rows };
        }
        case 'countries':
          return { head: ['Land', 'Studier'], rows: this.countryRows().all.map((c) => [c.name, fmt(c.n)]) };
        case 'results': {
          const r = d.results_rct;
          const tot = r.positive + r.unclear + r.null;
          const L = this.resultLabels();
          return {
            head: ['Resultat', 'Antall', 'Andel'],
            rows: [
              [L[0], fmt(r.positive), pct(r.positive / tot)],
              [L[1], fmt(r.unclear), pct(r.unclear / tot)],
              [L[2], fmt(r.null), pct(r.null / tot)],
              ['Totalt', fmt(tot), pct(1)],
            ],
          };
        }
      }
      return null;
    }

    partialYear() {
      const m = this.data && this.data.meta;
      return m && m.partial_year ? Number(m.partial_year) : null;
    }

    /* ---------- 1. Studier per år ---------- */

    renderPerYear(d) {
      this.clearPlot();
      const rows = d.per_year;
      const partial = this.partialYear();
      const lblAll = this.dataset.labelOther || 'Øvrige studier';
      const lblRct = this.dataset.labelRct || 'Randomiserte studier (RCT)';
      const lblPartial = this.dataset.partialLabel || 'hittil i år';

      const legend = [
        { label: lblRct, cls: 'rhlab-swatch--accent' },
        { label: lblAll },
      ];
      if (partial) legend.push({ label: `${partial}: ${lblPartial}`, cls: 'rhlab-swatch--partial' });
      this.legend(legend);

      const peak = rows.reduce((a, b) => (b.all > a.all ? b : a), rows[0]);
      const first = rows[0].y;
      const last = rows[rows.length - 1].y;
      const { svg, W, H } = this.newSvg(
        `Stolpediagram: antall publiserte studier per år fra ${first} til ${last}. ` +
          `Flest i ${peak.y} med ${fmt(peak.all)} studier, hvorav ${fmt(peak.rct)} randomiserte.` +
          (partial ? ` ${partial} gjelder bare deler av året.` : '')
      );
      const mobile = W < MOBILE;
      const m = { t: 24, r: 2, b: 26, l: 0 };
      const pw = W - m.l - m.r;
      const ph = H - m.t - m.b;
      const base = m.t + ph;
      const scale = niceScale(peak.all, mobile ? 2 : 4);
      const y = (v) => base - (v / scale.max) * ph;

      const gGrid = svgEl('g', { 'aria-hidden': 'true' }, svg);
      for (let v = 0; v <= scale.max + 0.001; v += scale.step) {
        const yy = Math.round(y(v)) + 0.5;
        svgEl('line', { class: v === 0 ? 'rhlab-svg__axis' : 'rhlab-svg__grid', x1: m.l, x2: W - m.r, y1: yy, y2: yy }, gGrid);
        if (v > 0) svgEl('text', { x: m.l, y: yy - 5 }, gGrid).textContent = fmt(v);
      }

      const n = rows.length;
      const band = pw / n;
      const w = clamp(band * 0.68, 2, 22);
      const gap = w > 6 ? 2 : 1.5;
      const r = Math.min(4, w / 2);
      const gCols = svgEl('g', { 'aria-hidden': 'true' }, svg);
      const items = [];
      const every = mobile ? 10 : 5;

      rows.forEach((row, i) => {
        const x = m.l + i * band + (band - w) / 2;
        const cx = x + w / 2;
        const g = svgEl('g', { class: 'rhlab-svg__col' + (this.animate ? ' rhlab-grow-y' : ''), style: `--i:${i}` }, gCols);
        const hAll = (row.all / scale.max) * ph;
        const hRct = (row.rct / scale.max) * ph;
        const isPartial = partial && row.y === partial;
        const otherTop = base - hAll;
        const otherH = hAll - hRct - (hRct > 0 ? gap : 0);
        if (otherH > 0.4) {
          svgEl('path', { class: 'rhlab-svg__bar' + (isPartial ? ' rhlab-svg__bar--partial' : ''), d: colPath(x, otherTop, w, otherH, r) }, g);
        }
        if (hRct > 0) {
          const rr = otherH > 0.4 ? 0 : r;
          svgEl('path', {
            class: 'rhlab-svg__bar rhlab-svg__bar--accent' + (isPartial ? ' rhlab-svg__bar--partial' : ''),
            d: colPath(x, base - hRct, w, hRct, rr),
          }, g);
        }
        if (row.y % every === 0) {
          svgEl('text', { x: cx, y: H - 6, 'text-anchor': cx < 14 ? 'start' : 'middle' }, svg).textContent = row.y;
        }
        const yearLabel = isPartial ? `${row.y} (${lblPartial})` : String(row.y);
        items.push({
          x: cx,
          y: otherTop,
          node: g,
          html:
            `<strong>${esc(yearLabel)}</strong>` +
            `<span>Alle studier <b>${fmt(row.all)}</b></span><span>Herav RCT <b>${fmt(row.rct)}</b></span>`,
          text: `${yearLabel}: ${fmt(row.all)} studier, herav ${fmt(row.rct)} RCT.`,
        });
      });

      // direct label on the peak year only
      const pi = rows.indexOf(peak);
      svgEl('text', {
        class: 'rhlab-svg__label rhlab-svg__label--strong',
        x: clamp(m.l + pi * band + band / 2, m.l + 12, W - 14),
        y: y(peak.all) - 7,
        'text-anchor': 'middle',
      }, svg).textContent = fmt(peak.all);

      this.interactive(
        svg,
        items,
        (px) => clamp(Math.floor((px - m.l) / band), 0, n - 1),
        'Studier per år. Bruk piltastene for å se tallene for hvert år.'
      );
    }

    /* ---------- 2. Evidenspyramide ---------- */

    renderPyramid(d) {
      this.clearPlot();
      const rows = d.pyramid.slice().reverse(); // top = strongest evidence
      const max = Math.max(...rows.map((r) => r.n));
      const accent = (this.dataset.accentKeys || 'rct,sr_ma').split(',').map((s) => s.trim());
      const wrap = htmlEl('div', 'rhlab-pyr', null, this.plot);
      wrap.setAttribute('role', 'img');
      wrap.setAttribute(
        'aria-label',
        'Evidenspyramide, fra sterkest evidens øverst til grunnforskning nederst: ' +
          rows.map((r) => `${r.label} ${fmt(r.n)}`).join(', ') + '.'
      );
      if (this.dataset.topLabel) htmlEl('p', 'rhlab-pyr__end rhlab-pyr__end--top', '↑ ' + this.dataset.topLabel, wrap).setAttribute('aria-hidden', 'true');
      rows.forEach((r, i) => {
        const row = htmlEl('div', 'rhlab-pyr__row' + (accent.includes(r.key) ? ' rhlab-pyr__row--accent' : ''), null, wrap);
        row.setAttribute('aria-hidden', 'true');
        const lab = htmlEl('p', 'rhlab-pyr__label', null, row);
        htmlEl('span', null, r.label, lab);
        htmlEl('span', 'rhlab-pyr__n', fmt(r.n), lab);
        const track = htmlEl('div', 'rhlab-pyr__track', null, row);
        const bar = htmlEl('span', 'rhlab-pyr__bar' + (this.animate ? ' rhlab-grow-c' : ''), null, track);
        bar.style.width = ((r.n / max) * 100).toFixed(2) + '%';
        bar.style.setProperty('--i', i);
      });
      if (this.dataset.bottomLabel) htmlEl('p', 'rhlab-pyr__end', this.dataset.bottomLabel, wrap).setAttribute('aria-hidden', 'true');
    }

    /* ---------- 3. Bølgelengder ---------- */

    parseRanges() {
      return (this.dataset.ranges || '')
        .split(',')
        .map((s) => s.trim().match(/^(\d{3,4})\s*[-–]\s*(\d{3,4})$/))
        .filter(Boolean)
        .map((mm) => [Number(mm[1]), Number(mm[2])].sort((a, b) => a - b));
    }

    renderWavelengths(d) {
      this.clearPlot();
      const wl = d.wavelengths;
      const { bin, from, to, counts } = wl;
      const ranges = this.parseRanges();
      const legend = [{ label: this.dataset.labelBars || 'Antall studier per 10 nm' }];
      if (ranges.length) legend.push({ label: this.dataset.rangeLabel || 'Mest studerte vinduer', cls: 'rhlab-swatch--band' });
      this.legend(legend);

      const ranked = counts.map((n, i) => ({ n, a: from + i * bin })).sort((a, b) => b.n - a.n);
      const { svg, W, H } = this.newSvg(
        `Histogram over bølgelengder brukt i studiene, ${from}–${to} nm i intervaller på ${bin} nm. ` +
          `Flest studier ved ${ranked[0].a}–${ranked[0].a + bin - 1} nm (${fmt(ranked[0].n)}) og ` +
          `${ranked[1].a}–${ranked[1].a + bin - 1} nm (${fmt(ranked[1].n)}).`
      );
      const mobile = W < MOBILE;
      const m = { t: 30, r: mobile ? 4 : 8, b: 58, l: 0 };
      const pw = W - m.l - m.r;
      const ph = H - m.t - m.b;
      const base = m.t + ph;
      const n = counts.length;
      const band = pw / n;
      const x = (nm) => m.l + ((nm - from) / bin) * band;
      const scale = niceScale(Math.max(...counts), 3);
      const y = (v) => base - (v / scale.max) * ph;
      const inRange = (a) => ranges.some(([lo, hi]) => a + bin - 1 >= lo && a <= hi);

      // grid
      const gGrid = svgEl('g', { 'aria-hidden': 'true' }, svg);
      for (let v = scale.step; v <= scale.max + 0.001; v += scale.step) {
        const yy = Math.round(y(v)) + 0.5;
        svgEl('line', { class: 'rhlab-svg__grid', x1: m.l, x2: W - m.r, y1: yy, y2: yy }, gGrid);
        svgEl('text', { x: m.l, y: yy - 5 }, gGrid).textContent = fmt(v);
      }

      // highlight bands
      const gBands = svgEl('g', { 'aria-hidden': 'true' }, svg);
      ranges.forEach(([lo, hi]) => {
        const x0 = x(clamp(Math.floor(lo / bin) * bin, from, to));
        const x1 = x(clamp(Math.floor(hi / bin) * bin + bin, from, to));
        if (x1 <= x0) return;
        svgEl('rect', { class: 'rhlab-svg__band', x: x0, y: m.t - 4, width: x1 - x0, height: base - m.t + 4 }, gBands);
        [x0, x1].forEach((xx) =>
          svgEl('line', { class: 'rhlab-svg__band-edge', x1: Math.round(xx) + 0.5, x2: Math.round(xx) + 0.5, y1: m.t - 4, y2: base }, gBands)
        );
        svgEl('text', { class: 'rhlab-svg__label', x: (x0 + x1) / 2, y: m.t - 10, 'text-anchor': 'middle' }, gBands).textContent =
          `${lo}–${hi}${mobile ? '' : ' nm'}`;
      });

      // bars
      const w = Math.max(1.5, Math.min(band - (band > 6 ? 2 : 1), 14));
      const r = Math.min(3, w / 2);
      const gBars = svgEl('g', { 'aria-hidden': 'true' }, svg);
      const items = [];
      const tops = [];
      counts.forEach((c, i) => {
        const a = from + i * bin;
        const bx = m.l + i * band + (band - w) / 2;
        const h = (c / scale.max) * ph;
        tops.push(base - h);
        const g = svgEl('g', { class: 'rhlab-svg__col' + (this.animate ? ' rhlab-grow-y' : ''), style: `--i:${Math.round(i / 2)}` }, gBars);
        if (h > 0) svgEl('path', { class: 'rhlab-svg__bar' + (inRange(a) ? ' rhlab-svg__bar--accent' : ''), d: colPath(bx, base - h, w, Math.max(h, 1), r) }, g);
        items.push({
          x: bx + w / 2,
          y: Math.min(base - h, base - 8),
          node: g,
          html: `<strong>${a}–${a + bin - 1} nm</strong><span>Studier <b>${fmt(c)}</b></span>`,
          text: `${a}–${a + bin - 1} nm: ${fmt(c)} studier.`,
        });
      });

      // baseline
      svgEl('line', { class: 'rhlab-svg__axis', x1: m.l, x2: W - m.r, y1: base + 0.5, y2: base + 0.5 }, svg);

      // peak annotations (non-colliding, strongest first)
      const peaks = (wl.peaks || []).slice(0, 5);
      const placed = [];
      peaks.forEach((p) => {
        const bi = Math.floor((p.nm - from) / bin);
        if (bi < 0 || bi >= n) return;
        const cx = m.l + bi * band + band / 2;
        const label = mobile ? String(p.nm) : `${p.nm} nm`;
        const tw = label.length * 6.2;
        const x0 = cx - tw / 2 - 2;
        const x1 = cx + tw / 2 + 2;
        let top = tops[bi];
        for (let j = 0; j < n; j++) {
          const jx = m.l + j * band + band / 2;
          if (jx + w / 2 >= x0 && jx - w / 2 <= x1) top = Math.min(top, tops[j]);
        }
        const ty = top - 6;
        const box = { x0, x1, y0: ty - 11, y1: ty + 2 };
        const hit = placed.some((b) => b.x0 < box.x1 && box.x0 < b.x1 && b.y0 < box.y1 && box.y0 < b.y1);
        if (hit || ty - 11 < m.t - 22 || x0 < m.l - 4 || x1 > W) return;
        placed.push(box);
        svgEl('text', { class: 'rhlab-svg__label rhlab-svg__label--strong', x: cx, y: ty, 'text-anchor': 'middle', 'aria-hidden': 'true' }, svg).textContent = label;
      });

      // spectrum strip
      const gid = `rhlab-spectrum-${this.uid}`;
      const defs = svgEl('defs', null, svg);
      const grad = svgEl('linearGradient', { id: gid, x1: 0, x2: 1, y1: 0, y2: 0 }, defs);
      [
        [400, '#8e86c9'],
        [460, '#7ea3cf'],
        [520, '#8fbf9a'],
        [575, '#e3cf7a'],
        [605, '#f08a5d'],
        [635, '#ef4444'],
        [680, '#c81e1e'],
        [760, '#7f1d1d'],
        [880, '#3f3a36'],
        [1100, '#2b2724'],
      ].forEach(([nm, c]) =>
        svgEl('stop', { offset: clamp((nm - from) / (to - from), 0, 1).toFixed(4), 'stop-color': c }, grad)
      );
      const sy = base + 6;
      svgEl('rect', { x: m.l, y: sy, width: pw, height: 6, rx: 3, fill: `url(#${gid})`, 'aria-hidden': 'true' }, svg);

      // x ticks
      const step = mobile ? 200 : 100;
      const firstTick = Math.ceil(from / step) * step;
      for (let t = firstTick; t <= to; t += step) {
        const tx = x(t);
        const last = t + step > to;
        const label = last ? `${t} nm` : String(t);
        const anchor = last && tx > W - 30 ? 'end' : tx - m.l < 14 ? 'start' : 'middle';
        svgEl('text', { x: tx, y: sy + 22, 'text-anchor': anchor }, svg).textContent = label;
      }

      // region labels (visible / NIR) around the 700 nm border
      if (from < 700 && to > 700) {
        const bx = Math.round(x(700)) + 0.5;
        svgEl('line', { class: 'rhlab-svg__leader', x1: bx, x2: bx, y1: sy + 30, y2: H - 2 }, svg);
        svgEl('text', { x: bx - 8, y: H - 5, 'text-anchor': 'end' }, svg).textContent = this.dataset.visibleLabel || '← Synlig lys';
        svgEl('text', { x: bx + 8, y: H - 5, 'text-anchor': 'start' }, svg).textContent = this.dataset.nirLabel || 'Nær-infrarødt (NIR) →';
      }

      this.interactive(
        svg,
        items,
        (px) => clamp(Math.floor((px - m.l) / band), 0, n - 1),
        'Bølgelengder. Bruk piltastene for å se antall studier per 10 nm.'
      );

      // peak list below the chart (once)
      if (!this.querySelector('.rhlab-peaks') && peaks.length) {
        const ul = htmlEl('ul', 'rhlab-peaks');
        const title = htmlEl('li', 'rhlab-peaks__title', this.dataset.peaksLabel || 'Mest brukte enkeltbølgelengder', ul);
        title.setAttribute('aria-hidden', 'true');
        ul.setAttribute('aria-label', this.dataset.peaksLabel || 'Mest brukte enkeltbølgelengder');
        peaks.forEach((p) => {
          const li = htmlEl('li', 'rhlab-peaks__item', null, ul);
          li.innerHTML = `<b>${p.nm} nm</b><span>${fmt(p.n)}</span>`;
        });
        this.plot.after(ul);
      }
    }

    /* ---------- 4. Land ---------- */

    countryRows() {
      const list = (this.data.countries || []).slice().sort((a, b) => b.n - a.n);
      const main = list.slice(0, 12);
      const extra = this.dataset.nordic === 'false' ? [] : list.slice(12).filter((c) => c.nordic);
      return { main, extra, all: main.concat(extra) };
    }

    renderCountries() {
      this.clearPlot();
      const { main, extra } = this.countryRows();
      const nordicOn = this.dataset.nordic !== 'false';
      const isHi = (c) => nordicOn && (c.highlight || c.name === (this.dataset.highlightName || 'Norge'));
      const max = main[0] ? main[0].n : 1;
      const box = htmlEl('div', null, null, this.plot);
      box.setAttribute('role', 'img');
      box.setAttribute(
        'aria-label',
        'Antall studier per land: ' + main.concat(extra).map((c) => `${c.name} ${fmt(c.n)}`).join(', ') + '.'
      );
      let i = 0;
      const list = (rows) => {
        const ul = htmlEl('ul', 'rhlab-hbars', null, box);
        ul.setAttribute('aria-hidden', 'true');
        rows.forEach((c) => {
          const li = htmlEl('li', 'rhlab-hbars__row' + (isHi(c) ? ' rhlab-hbars__row--accent' : ''), null, ul);
          htmlEl('span', 'rhlab-hbars__name', c.name, li).title = c.name;
          const track = htmlEl('span', 'rhlab-hbars__track', null, li);
          const bar = htmlEl('span', 'rhlab-hbars__bar' + (this.animate ? ' rhlab-grow-x' : ''), null, track);
          bar.style.width = `calc((100% - 3.75rem) * ${(c.n / max).toFixed(4)})`;
          bar.style.setProperty('--i', i++);
          htmlEl('span', 'rhlab-hbars__value', fmt(c.n), track);
        });
      };
      list(main);
      if (extra.length) {
        htmlEl('p', 'rhlab-hbars__group', this.dataset.groupLabel || 'Norden', box).setAttribute('aria-hidden', 'true');
        list(extra);
      }
    }

    /* ---------- 5. Resultater i RCT-er (bevisst nedtonet) ---------- */

    resultLabels() {
      return [
        this.dataset.labelPositive || 'Positivt resultat',
        this.dataset.labelUnclear || 'Uklart eller blandet',
        this.dataset.labelNull || 'Ingen effekt eller negativt',
      ];
    }

    renderResults(d) {
      this.clearPlot();
      const r = d.results_rct;
      const vals = [r.positive, r.unclear, r.null];
      const tot = vals.reduce((a, b) => a + b, 0) || 1;
      const L = this.resultLabels();
      const totalTpl = this.dataset.totalLabel || 'Basert på {n} randomiserte kontrollerte studier (RCT) på mennesker med vurdert resultat';
      htmlEl('p', 'rhlab-split__total', totalTpl.replace('{n}', fmt(tot)), this.plot);
      const bar = htmlEl('div', 'rhlab-split__bar' + (this.animate ? ' rhlab-grow-x' : ''), null, this.plot);
      bar.setAttribute('role', 'img');
      bar.setAttribute('aria-label', L.map((l, i) => `${l}: ${pct(vals[i] / tot)} (${fmt(vals[i])})`).join(', ') + '.');
      vals.forEach((v, i) => {
        const seg = htmlEl('span', `rhlab-split__seg rhlab-split__seg--${i + 1}`, null, bar);
        seg.style.flex = `${v} 1 0`;
      });
      const ul = htmlEl('ul', 'rhlab-split__legend', null, this.plot);
      ul.setAttribute('aria-hidden', 'true');
      vals.forEach((v, i) => {
        const li = htmlEl('li', 'rhlab-split__item', null, ul);
        li.innerHTML =
          `<span class="rhlab-swatch rhlab-swatch--tone-${i + 1}"></span><span>${esc(L[i])}</span>` +
          `<span class="rhlab-split__nums">${pct(v / tot)}<small>n = ${fmt(v)}</small></span>`;
      });
    }
  }

  customElements.define('rh-lab-chart', RHLabChart);
})();
